import {
    emptyUserInstancePrefs,
    mergeUserInstancePrefsPatch,
    readUserInstancePrefsDoc,
} from './kronosPrefs/userInstancePrefs';
import { readPersistedSessionIds } from './kronosPrefs/sessionIds';

/** Top-level field under `kronos.user.<uid>.instance.<iid>` JSON. */
export const HEALTH_CHECK_RESUME_SNOOZE_PREF_FIELD = 'healthCheckResumeSnooze';

/**
 * Local midnight at the start of the calendar day after `from`.
 * @param {Date} [from]
 * @returns {Date}
 */
export function start_of_tomorrow(from = new Date()) {
    const next = new Date(from.getTime());
    next.setHours(24, 0, 0, 0);
    return next;
}

/**
 * Snooze signature: instance + failed groups (not the health-log id).
 *
 * @param {string} instance_id
 * @param {string} groups_signature  e.g. `wordpress:2|retention:1`
 * @returns {string}
 */
export function build_health_check_resume_snooze_signature(instance_id, groups_signature) {
    if (!groups_signature)
        return '';
    return `${instance_id || 'none'}|${groups_signature}`;
}

/**
 * @param {{ signature?: unknown, until?: unknown } | null | undefined} raw
 * @returns {{ signature: string, until: number } | null}
 */
function normalize_snooze(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const signature = typeof raw.signature === 'string' ? raw.signature : '';
    const until = Number(raw.until);
    if (!signature || !Number.isFinite(until))
        return null;
    return { signature, until };
}

/**
 * @param {string} [instance_id]
 * @returns {{ userId: string|null, instanceId: string }}
 */
function resolve_session(instance_id) {
    const session = readPersistedSessionIds();
    return {
        userId: session.userId,
        instanceId: instance_id || session.instanceId,
    };
}

/**
 * @param {string} [instance_id]
 * @returns {{ signature: string, until: number } | null}
 */
export function read_health_check_resume_snooze(instance_id) {
    const { userId, instanceId } = resolve_session(instance_id);
    if (!userId)
        return null;

    const doc = readUserInstancePrefsDoc(userId, instanceId) || emptyUserInstancePrefs();
    return normalize_snooze(doc[HEALTH_CHECK_RESUME_SNOOZE_PREF_FIELD]);
}

/**
 * @param {string} [instance_id]
 */
export function clear_health_check_resume_snooze(instance_id) {
    const { userId, instanceId } = resolve_session(instance_id);
    if (!userId)
        return;
    mergeUserInstancePrefsPatch(userId, instanceId, {
        [HEALTH_CHECK_RESUME_SNOOZE_PREF_FIELD]: null,
    });
}

/**
 * Hide the panel only while the snooze is still valid for this exact failure signature
 * (includes instance id). A new / different signature always shows the panel again.
 *
 * @param {string} signature
 * @param {number} [now]
 * @param {string} [instance_id]
 * @returns {boolean}
 */
export function is_health_check_resume_snoozed(signature, now = Date.now(), instance_id) {
    if (!signature)
        return false;

    const snooze = read_health_check_resume_snooze(instance_id);
    if (!snooze)
        return false;

    if (snooze.until <= now) {
        clear_health_check_resume_snooze(instance_id);
        return false;
    }

    return snooze.signature === signature;
}

/**
 * @param {string} signature
 * @param {Date} [from]
 * @param {string} [instance_id]
 */
export function persist_health_check_resume_snooze(signature, from = new Date(), instance_id) {
    if (!signature)
        return;

    const { userId, instanceId } = resolve_session(instance_id);
    if (!userId)
        return;

    mergeUserInstancePrefsPatch(userId, instanceId, {
        [HEALTH_CHECK_RESUME_SNOOZE_PREF_FIELD]: {
            signature,
            until: start_of_tomorrow(from).getTime(),
        },
    });
}
